import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Loader2, Mail, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg, fmtDate, inr, Money } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { DataTable, DateRangeFilter, EntityCombobox, Field, PageHeader, SearchBox } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MODES = ["Cash", "UPI", "Bank Transfer", "Card", "Razorpay", "Other"];

export default function Payments() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [range, setRange] = useState({ from: "", to: "" });
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payments", { params: { q, page, limit: 25, from_date: range.from, to_date: range.to } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [q, page, range]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/customers?limit=200").then((r) => setCustomers(r.data.items)).catch(() => {});
    if (params.get("new") === "1") openNew();
  }, []);

  const openNew = () => {
    setForm({ date: new Date().toISOString().slice(0, 10), customer_id: "", invoice_id: "",
      booking_id: "", amount: 0, mode: "Cash", reference: "", notes: "" });
    setOutstanding([]);
    setOpen(true);
  };

  const onCustomer = async (cid) => {
    setForm((f) => ({ ...f, customer_id: cid, invoice_id: "", booking_id: "" }));
    if (!cid) return setOutstanding([]);
    const r = await api.get("/invoices", { params: { customer_id: cid, limit: 200 } });
    setOutstanding(r.data.items.filter((i) => ["issued", "partial"].includes(i.status) && i.balance > 0));
  };

  const submit = async () => {
    if (!form.customer_id || form.amount <= 0) return toast.error("Select customer and enter amount");
    setSaving(true);
    try {
      const r = await api.post("/payments", form);
      (r.data.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Receipt ${r.data.payment.receipt_no} recorded`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete receipt ${p.receipt_no}? Invoice/booking balances will be restored.`)) return;
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Payment deleted and balances restored");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title="Payments & Receipts" subtitle="Collections linked to customers, bookings and invoices"
        testid="payments-header"
        actions={
          <>
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search receipt, reference, customer…" testid="payments-search" />
            <DateRangeFilter from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} testid="payments-date-filter" />
            {can(user?.role, "payments", true) && (
              <Button className="bg-[#0D4F4A] hover:bg-[#136A63]" onClick={openNew} data-testid="payments-create-button">
                <Plus className="h-4 w-4 mr-1" /> Add Payment
              </Button>
            )}
          </>
        } />
      <DataTable testid="payments-table" loading={loading}
        columns={[
          { key: "receipt_no", label: "Receipt", render: (r) => <span className="font-semibold text-[#0D4F4A]">{r.receipt_no}{r.is_demo && <span className="ml-1 text-[10px] font-bold text-[#8a6d14]">DEMO</span>}</span> },
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "customer_name", label: "Customer" },
          { key: "invoice_no", label: "Invoice", render: (r) => r.invoice_no || <span className="text-xs text-[#5C6B69]">Advance</span> },
          { key: "mode", label: "Mode" },
          { key: "reference", label: "Reference", render: (r) => r.reference || "—" },
          { key: "amount", label: "Amount", align: "right", render: (r) => <Money value={r.amount} className="font-semibold" /> },
          { key: "_a", label: "", align: "right", render: (r) => (
            <div className="flex justify-end gap-1">
              <Button size="icon" variant="ghost" className="h-7 w-7" title="Receipt PDF"
                onClick={(e) => { e.stopPropagation(); download(`/payments/${r.id}/receipt-pdf`, "receipt.pdf"); }}
                data-testid={`payment-receipt-${r.id}`}><Download className="h-3.5 w-3.5" /></Button>
              {can(user?.role, "payments", true) && (
                <>
                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Email receipt"
                    onClick={async (e) => { e.stopPropagation();
                      try {
                        const res = await api.post(`/payments/${r.id}/send-email`);
                        toast.success(`Receipt emailed to ${res.data.to}`);
                      } catch (err) { toast.error(errMsg(err)); } }}
                    data-testid={`payment-email-${r.id}`}><Mail className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                    onClick={(e) => { e.stopPropagation(); remove(r); }}
                    data-testid={`payment-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </>
              )}
            </div>) },
        ]}
        rows={data.items} page={data.page} pages={data.pages} total={data.total} onPage={setPage}
        empty="No payments recorded yet." />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl" data-testid="payment-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Record Payment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field label="Customer" required>
                <EntityCombobox testid="payment-customer-select" value={form.customer_id || ""} onChange={onCustomer}
                  items={customers.map((c) => ({ value: c.id, label: c.name, sub: c.phone }))}
                  placeholder="Select customer…" />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Against Invoice" hint={form.customer_id ? (outstanding.length ? `${outstanding.length} invoice(s) with balance` : "No outstanding invoices — payment will be an advance") : "Select customer first"}>
                <EntityCombobox testid="payment-invoice-select" value={form.invoice_id || ""}
                  onChange={(v) => {
                    const inv = outstanding.find((i) => i.id === v);
                    setForm((f) => ({ ...f, invoice_id: v, booking_id: inv?.booking_id || "",
                      amount: inv ? inv.balance : f.amount }));
                  }}
                  items={[{ value: "", label: "— Advance / on account —" }, ...outstanding.map((i) => ({
                    value: i.id, label: `${i.invoice_no} · balance ${inr(i.balance)}`, sub: `${fmtDate(i.date)} · total ${inr(i.grand_total)}` }))]}
                  placeholder="Select invoice…" />
              </Field>
            </div>
            <Field label="Amount (₹)" required>
              <Input type="number" min="0" value={form.amount ?? 0} className="bg-white border-[#E5E0D8]"
                onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} data-testid="payment-form-amount" />
            </Field>
            <Field label="Date">
              <Input type="date" value={form.date || ""} className="bg-white border-[#E5E0D8]"
                onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-form-date" />
            </Field>
            <Field label="Mode">
              <Select value={form.mode || "Cash"} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="payment-form-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Reference / UTR">
              <Input value={form.reference || ""} className="bg-white border-[#E5E0D8]"
                onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payment-form-reference" />
            </Field>
            <div className="col-span-2">
              <Field label="Notes">
                <Textarea value={form.notes || ""} className="bg-white border-[#E5E0D8]"
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payment-form-notes" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="payment-form-submit">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
